import {ComponentRef, isSignal, reflectComponentType} from '@angular/core';
import {NgbModalRef} from '@ng-bootstrap/ng-bootstrap';

/**
 * Gives a created component a value by key: an input through setInput, as a binding would (a signal
 * input cannot be assigned), anything else assigned, skipping getter-only properties (derived inputs).
 * An input is named by its property or by its public name: an input the component also writes is
 * aliased, and the linked signal it writes carries the public name.
 */
export const inputGiver = (ref: ComponentRef<unknown>): ((key: string, value: unknown) => void) => {
  const instance = ref.instance as Record<string, unknown>;
  // setInput takes an input's public name, which an alias makes differ from its property.
  const publicNames = new Map<string, string>();
  for (const input of reflectComponentType(ref.componentType)?.inputs || []) {
    publicNames.set(input.propName, input.templateName);
    publicNames.set(input.templateName, input.templateName);
  }
  return (key, value) => {
    if (publicNames.has(key)) {
      ref.setInput(publicNames.get(key), value);
      return;
    }
    if (isSignal(instance[key])) {
      // Assigning would put a plain value where the component reads a signal.
      throw new Error(`${key} is a signal but not an input: it cannot be given a value.`);
    }
    try {
      instance[key] = value;
    } catch {
      // Property has no setter — leave the component's derived value alone.
    }
  };
};

/**
 * The ComponentRef of the component a modal was opened with. NgbModalRef keeps it private (it only
 * hands out its instance, and setting an input takes the ref), so this reads ng-bootstrap's own
 * field, and fails loudly if an upgrade moves it rather than open a modal without its inputs.
 */
export const modalComponentRef = <T>(ref: NgbModalRef): ComponentRef<T> => {
  const componentRef = (ref as unknown as {_contentRef?: {componentRef?: ComponentRef<T>}})._contentRef?.componentRef;
  if (!componentRef) {
    throw new Error('NgbModalRef holds no ComponentRef: the modal cannot be given its inputs.');
  }
  return componentRef;
};
